/**
 * Parses a BCP 47 language tag into a LocaleInfo object.
 * Uses Intl.Locale if available, otherwise basic parsing.
 */
import { LocaleCalendar, LocaleInfoArray, LocaleTag } from '@frontend/Models/Locale/LocaleModels';

const parseLocaleTag = (tag: string): LocaleTag => {
  try {
    // Use Intl.Locale for robust parsing if available
    const locale = new Intl.Locale(tag);
    const languageCode = locale.language ?? tag.split('-')[0] ?? 'en';
    // Basic RTL check (expand if needed)
    const rtlLanguages = ['ar', 'he', 'fa', 'ur', 'yi', 'dv', 'ps', 'sd'];
    return {
      languageTag: locale.toString(),
      languageCode: languageCode,
      regionCode: locale.region ?? null,
      isRTL: rtlLanguages.includes(languageCode),
    };
  } catch (e) {
    // Fallback for environments without Intl.Locale or simple tags
    const parts = tag.split(/[-_]/);
    const languageCode = parts[0] ?? 'en';
    const rtlLanguages = ['ar', 'he', 'fa', 'ur', 'yi', 'dv', 'ps', 'sd'];
    return {
      languageTag: tag,
      languageCode: languageCode,
      regionCode: parts[1]?.toUpperCase() ?? null,
      isRTL: rtlLanguages.includes(languageCode),
    };
  }
};

/**
 * This service is used to get localization information using Web APIs.
 */
export const LocalizationService = {
  /**
   * Gets the user's primary calendar system based on their locale.
   * Note: Web APIs don't typically provide a list of *preferred* calendars like Expo.
   * This returns the default calendar for the primary locale.
   */
  getCalendars: (): LocaleCalendar[] => {
    try {
      // Get the primary language tag from the browser
      const primaryLangTag = navigator.language || 'en';
      // Use Intl.Locale to determine the default calendar
      const locale = new Intl.Locale(primaryLangTag);
      // Expo returns an array, so we mimic that structure
      return [{ calendar: locale.calendar ?? 'gregory' }]; // Default to Gregorian
    } catch (e) {
      console.error('Error getting calendar info:', e);
      // Fallback if Intl.Locale fails or isn't supported
      return [{ calendar: 'gregory' }];
    }
  },

  /**
   * Gets the user's preferred locales (languages) from the browser.
   * Returns an array ordered by preference.
   */
  getLocales: (): LocaleInfoArray => {
    try {
      // navigator.languages provides an ordered list of preferred languages
      const languages = navigator.languages || [navigator.language || 'en'];
      // Map the language tags to the LocaleInfo structure
      const mapped = languages.map(parseLocaleTag);
      return mapped;
    } catch (e) {
      console.error('Error getting locales:', e);
      // Basic fallback in case of unexpected errors
      return [parseLocaleTag('en')];
    }
  },
};
